/**
 * homeAggregates.ts — Agregados de la HOME (Centro de inteligencia) computados
 * EN EL FRONTEND a partir de los endpoints que ya existen:
 *   - `/listings/inbox`  → calidad de base, distribución por categoría, mis
 *                          zonas, valoración media, oportunidades del día.
 *   - `/listings/jobs`   → tendencia de extracciones, "continúa donde lo dejaste".
 *
 * Funciones PURAS y testeables. No inventan datos: si un agregado no es
 * calculable con lo disponible, se omite o se marca. Reutiliza el módulo
 * `opportunityScore` para el eje de oportunidad.
 *
 * @module lib/homeAggregates
 */

import type { InboxItem, UserJobSummary } from '@/services/listingsService'
import { categoryLabel } from '@/lib/categoryLabel'
import {
  opportunityScore,
  scoreBand,
  leadSignals,
  contactability,
  type OppBand,
  type LeadSignal,
  type Contactability,
} from '@/lib/opportunityScore'

// ---------------------------------------------------------------------------
// Helpers de formato
// ---------------------------------------------------------------------------

/** Etiqueta ES de una categoría GMB (reusa el util `categoryLabel`). */
export function prettifyCategory(slug: string | null | undefined): string {
  return categoryLabel(slug)
}

/** Ciudad legible de un InboxItem. */
function cityOf(item: InboxItem): string {
  return (
    item.address_components?.city ||
    item.address_components?.region ||
    'Sin zona'
  )
}

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']

/** Fecha relativa corta en español ("hace 2 h", "ayer", "30 may"). */
export function relativeDate(iso: string | null, now: number = Date.now()): string {
  if (!iso) return '—'
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return '—'
  const diffMs = now - t
  const min = Math.floor(diffMs / 60000)
  if (min < 1) return 'ahora'
  if (min < 60) return `hace ${min} min`
  const h = Math.floor(min / 60)
  if (h < 24) return `hace ${h} h`
  const d = Math.floor(h / 24)
  if (d === 1) return 'ayer'
  if (d < 7) return `hace ${d} d`
  const date = new Date(t)
  return `${date.getDate()} ${MESES[date.getMonth()]}`
}

// ---------------------------------------------------------------------------
// Tipos de salida
// ---------------------------------------------------------------------------

export interface QualityPct {
  email: number
  web: number
  phone: number
  claimed: number
}

export interface CategorySlice {
  label: string
  count: number
}

export interface ZoneSlice {
  city: string
  count: number
  band: OppBand
}

export interface RatingAgg {
  avg: number
  totalReviews: number
  pctHigh: number
  /** Distribución 1★..5★ (índice 0 = 1★). */
  distribution: number[]
}

export interface Opportunity {
  placeId: string
  title: string
  category: string
  city: string
  score: number
  band: OppBand
  signals: LeadSignal[]
  contact: Contactability
}

export interface HomeAggregates {
  totalLeads: number
  sampleSize: number
  quality: QualityPct
  categories: CategorySlice[]
  rating: RatingAgg
  zones: ZoneSlice[]
  opportunities: Opportunity[]
}

function pct(n: number, total: number): number {
  return total > 0 ? Math.round((n / total) * 100) : 0
}

/**
 * Computa todos los agregados de la Home a partir de la muestra de inbox.
 * `totalLeads` debe ser el `total` real del endpoint; los porcentajes y
 * distribuciones se calculan sobre los `items` cargados (muestra).
 */
export function computeHomeAggregates(
  items: InboxItem[],
  totalLeads: number,
): HomeAggregates {
  const n = items.length

  // Calidad de base
  let withEmail = 0,
    withWeb = 0,
    withPhone = 0,
    claimed = 0
  // Categorías
  const catMap = new Map<string, number>()
  // Zonas (count + acumulado de score para banda media)
  const zoneAgg = new Map<string, { count: number; scoreSum: number }>()
  // Rating
  let ratingSum = 0,
    ratingN = 0,
    reviewsSum = 0,
    high = 0
  const dist = [0, 0, 0, 0, 0]

  for (const it of items) {
    if (it.email_primary && it.email_primary.trim()) withEmail++
    if (it.url && it.url.trim()) withWeb++
    if (it.phone && it.phone.trim()) withPhone++
    if (it.is_claimed === true) claimed++

    const cat = prettifyCategory(it.category_primary)
    catMap.set(cat, (catMap.get(cat) ?? 0) + 1)

    const sc = opportunityScore(it)
    const city = cityOf(it)
    const z = zoneAgg.get(city) ?? { count: 0, scoreSum: 0 }
    z.count++
    z.scoreSum += sc.score
    zoneAgg.set(city, z)

    if (it.rating_value != null) {
      ratingSum += it.rating_value
      ratingN++
      if (it.rating_value >= 4.5) high++
      const bucket = Math.min(5, Math.max(1, Math.round(it.rating_value)))
      dist[bucket - 1]++
    }
    if (it.rating_votes_count != null) reviewsSum += it.rating_votes_count
  }

  const categories: CategorySlice[] = [...catMap.entries()]
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count)

  // Top 5 categorías + agregado "Otras"
  let topCats = categories.slice(0, 5)
  const restCount = categories.slice(5).reduce((s, c) => s + c.count, 0)
  if (restCount > 0) topCats = [...topCats, { label: 'Otras', count: restCount }]

  const zones: ZoneSlice[] = [...zoneAgg.entries()]
    .map(([city, z]) => ({
      city,
      count: z.count,
      band: scoreBand(Math.round(z.scoreSum / z.count)),
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 6)

  // Oportunidades del día: score alto, más recientes primero.
  const opportunities: Opportunity[] = items
    .map((it) => {
      const sc = opportunityScore(it)
      return {
        placeId: it.place_id,
        title: it.title,
        category: prettifyCategory(it.category_primary),
        city: cityOf(it),
        score: sc.score,
        band: sc.band,
        signals: leadSignals(it),
        contact: contactability(it),
        _at: it.first_extracted_at,
      }
    })
    .filter((o) => o.band === 'high')
    .sort((a, b) => {
      const ta = new Date(a._at).getTime() || 0
      const tb = new Date(b._at).getTime() || 0
      if (tb !== ta) return tb - ta
      return b.score - a.score
    })
    .slice(0, 4)
    .map(({ _at, ...rest }) => rest)

  return {
    totalLeads,
    sampleSize: n,
    quality: {
      email: pct(withEmail, n),
      web: pct(withWeb, n),
      phone: pct(withPhone, n),
      claimed: pct(claimed, n),
    },
    categories: topCats,
    rating: {
      avg: ratingN > 0 ? ratingSum / ratingN : 0,
      totalReviews: reviewsSum,
      pctHigh: pct(high, ratingN),
      distribution: dist,
    },
    zones,
    opportunities,
  }
}

// ---------------------------------------------------------------------------
// Jobs: tendencia + continúa donde lo dejaste
// ---------------------------------------------------------------------------

export interface TrendBucket {
  label: string
  /** Leads extraídos en la semana. */
  leads: number
}

export interface TrendAgg {
  buckets: TrendBucket[]
  totalLeads: number
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000

/**
 * Agrupa los jobs en las últimas `weeks` semanas (por created_at), sumando
 * leads extraídos. Devuelve buckets en orden cronológico (más antiguo →
 * más reciente), etiquetados S1..Sn.
 */
export function computeTrend(
  jobs: UserJobSummary[],
  weeks = 8,
  now: number = Date.now(),
): TrendAgg {
  const buckets: number[] = new Array(weeks).fill(0)
  let total = 0
  for (const j of jobs) {
    const t = new Date(j.created_at).getTime()
    if (Number.isNaN(t)) continue
    const weeksAgo = Math.floor((now - t) / WEEK_MS)
    if (weeksAgo < 0 || weeksAgo >= weeks) continue
    const idx = weeks - 1 - weeksAgo
    const leads = j.result_listings_count ?? 0
    buckets[idx] += leads
    total += leads
  }
  return {
    buckets: buckets.map((leads, i) => ({ label: `S${i + 1}`, leads })),
    totalLeads: total,
  }
}

export interface RecentSearch {
  id: string
  category: string
  zone: string
  status: UserJobSummary['status']
  leads: number
  date: string
}

/** Mapea los jobs recientes a filas de "Continúa donde lo dejaste". */
export function recentSearches(
  jobs: UserJobSummary[],
  limit = 4,
  now: number = Date.now(),
): RecentSearch[] {
  return [...jobs]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, limit)
    .map((j) => {
      const cats = j.query?.categories ?? []
      const zone =
        j.query?.zone_query && 'name' in j.query.zone_query
          ? j.query.zone_query.name
          : 'Zona'
      return {
        id: j.id,
        category: cats.length ? prettifyCategory(cats[0]) : 'Extracción',
        zone,
        status: j.status,
        leads: j.result_listings_count ?? 0,
        date: relativeDate(j.created_at, now),
      }
    })
}

// ---------------------------------------------------------------------------
// Puntos geográficos por municipio (para el mapa real)
// ---------------------------------------------------------------------------

export interface ZonePoint {
  city: string
  /** Centroide de los leads del municipio. */
  lat: number
  lng: number
  count: number
  band: OppBand
}

/**
 * Agrupa los leads CON coordenadas por municipio y calcula, por municipio: el
 * centroide (media de lat/lng), el nº de leads y la banda de oportunidad
 * agregada (a partir de la media del Opportunity Score). Los leads sin coords
 * (lat/lng null) se descartan — no se geocodifican.
 */
export function computeZonePoints(items: InboxItem[]): ZonePoint[] {
  const acc = new Map<
    string,
    { latSum: number; lngSum: number; count: number; scoreSum: number }
  >()
  for (const it of items) {
    if (typeof it.latitude !== 'number' || typeof it.longitude !== 'number') continue
    if (!Number.isFinite(it.latitude) || !Number.isFinite(it.longitude)) continue
    const city = cityOf(it)
    const a = acc.get(city) ?? { latSum: 0, lngSum: 0, count: 0, scoreSum: 0 }
    a.latSum += it.latitude
    a.lngSum += it.longitude
    a.count += 1
    a.scoreSum += opportunityScore(it).score
    acc.set(city, a)
  }
  return [...acc.entries()].map(([city, a]) => ({
    city,
    lat: a.latSum / a.count,
    lng: a.lngSum / a.count,
    count: a.count,
    band: scoreBand(Math.round(a.scoreSum / a.count)),
  }))
}

/** Bounds [[minLng,minLat],[maxLng,maxLat]] de los puntos, o null si vacío. */
export function pointsBounds(
  points: { lat: number; lng: number }[],
): [[number, number], [number, number]] | null {
  if (points.length === 0) return null
  let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity
  for (const p of points) {
    minLat = Math.min(minLat, p.lat); maxLat = Math.max(maxLat, p.lat)
    minLng = Math.min(minLng, p.lng); maxLng = Math.max(maxLng, p.lng)
  }
  return [[minLng, minLat], [maxLng, maxLat]]
}

/** Días restantes hasta una fecha ISO (para el anillo de cupo). */
export function daysUntil(iso: string | null, now: number = Date.now()): number {
  if (!iso) return 0
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return 0
  return Math.max(0, Math.ceil((t - now) / (24 * 60 * 60 * 1000)))
}
